/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Amorim API v1
 * Amorim API v1 — Public-facing endpoints (CMS, authentication, authenticated user resources, reservations).
 *
 * **Authentication**
 *
 * - All requests require an `Api-Key` header.
 * - Endpoints that operate on the authenticated user additionally require `Authorization: Bearer <jwt>`.
 * OpenAPI spec version: v1
 */
import type {
  EventDateFacetDto,
  EventDetailDto,
  EventFacetDto,
  EventListItemDto,
  EventListItemDtoPagedResult,
  EventVenueFacetDto,
  GetEventCategoriesV1Params,
  GetEventDatesV1Params,
  GetEventTypesV1Params,
  GetEventVenuesV1Params,
  GetEventsV1Params,
  GetRelatedEventsV1Params,
  GetWhatsOnEventsV1Params
} from '../model';

import { customFetch } from '../../../custom-fetch.ts';

export type getEventsV1Response200 = {
  data: EventListItemDtoPagedResult
  status: 200
}

export type getEventsV1Response400 = {
  data: void
  status: 400
}

export type getEventsV1Response401 = {
  data: void
  status: 401
}

export type getEventsV1ResponseSuccess = (getEventsV1Response200) & {
  headers: Headers;
};
export type getEventsV1ResponseError = (getEventsV1Response400 | getEventsV1Response401) & {
  headers: Headers;
};

export type getEventsV1Response = (getEventsV1ResponseSuccess | getEventsV1ResponseError)

export const getGetEventsV1Url = (params?: GetEventsV1Params,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["eventType","eventCategory","venueId","datePreset","date","visibility"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/events?${stringifiedParams}` : `/api/v1/events`
}

/**
 * Returns published brand events for the events list/calendar, soonest start first; continuous campaigns (recurring daily or several days a week) close the list, undated.
 * "Undated" describes the LAYOUT only. Campaigns are filtered by date like any other event: date and datePreset match a campaign on the days its recurrence actually runs, so selecting a Monday no longer returns a Sunday-only brunch.
 * This is the calendar surface. For a "next N events" rail (home page, venue detail) use GET /events/whats-on, which resolves each event's real next date instead of ordering by what the database happens to hold.
 * By default only events starting today (Europe/Lisbon) or later are returned — ongoing campaigns (recurrence still active) are kept, and an explicit fromDate or date overrides the floor.
 *
 * Multi-date events (contract v1.12): date and venue filters, the floor and the ordering consider every public occurrence of the event, and the card's startDateTime is the next upcoming occurrence when one exists (the detail's occurrences[] has the full list). In the default window (no fromDate/date) a campaign's card is dated by its next run day, derived from the recurrence — never by its long-past original start.
 *
 * days[] (per card): the Europe/Lisbon days the card belongs under, ascending — { date, startDateTime, endDateTime, occurrenceId, status } per day. occurrenceId is the occurrence behind the day (null on a day derived from a campaign's recurrence, and on events without occurrences); status is that occurrence's, else the event's — Full marks only that day sold out. A campaign lists every day its recurrence runs (Salesforce sends no future occurrence for a campaign), at its own Lisbon hour and length; a multi-date event lists each public occurrence day; a span lists every day it covers (each with the span's own start/end). From today (or the explicit fromDate/date) up to 60 days ahead, clipped to the active date filters (datePreset, date, fromDate/toDate) — exactly the days GET /events/dates lights for the same filters. Render a card under each days[].date and order a day's cards by days[].startDateTime; there is no need to request campaigns separately. The recurrence (frequency, interval, weekdays — the anchor's weekday when none — and end date) is expanded by one rule shared with GET /events/dates, the detail's days[] and GET /events/whats-on.
 *
 * Query Parameters:
 * - page: Page number (default: 1)
 * - pageSize: Items per page (default: 50, max: 200)
 * - fromDate / toDate: ISO-8601 bounds on start date (optional; fromDate overrides the today floor). Recurrence-aware like date: a campaign matches when its recurrence runs inside the window, not only on its original start
 * - eventType: event type, repeatable (eventType=..&eventType=..) — OR within, AND with other filters (optional)
 * - eventCategory: category (the filter-chip value), repeatable — OR within, AND with other filters (optional)
 * - q: free-text search over title, type and venue names; accent- and case-insensitive (optional)
 * - hubKey: location node key — only events at venues in that hub (optional; invalid GUIDs are ignored)
 * - venueId: Salesforce venue id from GET /events/venues, repeatable; a venue-level id also matches its sub-venues' events (optional)
 * - datePreset: repeatable presets, OR'd: today, tomorrow, thisWeek, nextWeek, everyday, mondayToFriday, weekend, sunday. Computed in Europe/Lisbon, weeks start Monday, everyday = no day restriction. Combines with fromDate/toDate (AND). Unknown values return 400. Recurrence-aware, like date — "sunday" no longer returns a Thursday-only campaign.
 * - date: explicit Europe/Lisbon day (yyyy-MM-dd), repeatable, max 62 — only events on those days (OR between days, AND with other filters; overrides the today floor). Invalid values return 400. A campaign matches a day when its recurrence runs on it (frequency/weekdays, Europe/Lisbon, bounded by its start and recurrence end), not only on its original start date. Two documented approximations: the recurrence INTERVAL is treated as 1 ("every other Friday" also matches the off Friday — no production campaign uses an interval other than 1), and a campaign whose weekdays have not been re-synced since the mask was introduced still matches every day, as it did before.
 * - isCampaign: true = campaigns only, false = one-off events only (optional). A campaign is a recurring event (has a recurrence descriptor).
 * - allowsBooking: true = only bookable events, false = only non-bookable (optional). An event is bookable unless its bookingMode is None — and an absent bookingMode means None (v1.12 §3.2).
 * - includeRestricted: true = drop the visibility gate and return every published event, Members Only and Invite Only included — the FE renders those cards locked (optional; default keeps the caller-scoped rules). The detail endpoint has no visibility gate (any published event opens); the register keeps it.
 * - excludeExternal: true = leave out events ticketed elsewhere (bookingMode External, booked on the partner's site) — for surfaces that only show what can be booked in the app (optional; default includes them).
 * - groupId: lists the events of one group — the "where" step of a grouped card (one entry per venue/edition, each with its own menu/price/detail). Without it the listing returns ONE CARD PER GROUP (the isGroupMain event fronts it) — never render one card per event of a group (v1.12 §3.0).
 * - visibility: visibility code, repeatable — Open, Members Only, Invite Only; OR within, AND with other filters (optional). E.g. visibility=Members Only for the "Members Only" filter. It only narrows what the caller may already see: without includeRestricted, a non-member asking for Members Only gets nothing.
 * @summary List brand events (calendar cards)
 */
export const getEventsV1 = async (params?: GetEventsV1Params, options?: Parameters<typeof customFetch>[1]): Promise<getEventsV1Response> => {

  return customFetch<getEventsV1Response>(getGetEventsV1Url(params),
  {
    ...options,
    method: 'GET'


  }
);}


export type getEventDetailV1Response200 = {
  data: EventDetailDto
  status: 200
}

export type getEventDetailV1Response401 = {
  data: void
  status: 401
}

export type getEventDetailV1Response404 = {
  data: void
  status: 404
}

export type getEventDetailV1ResponseSuccess = (getEventDetailV1Response200) & {
  headers: Headers;
};
export type getEventDetailV1ResponseError = (getEventDetailV1Response401 | getEventDetailV1Response404) & {
  headers: Headers;
};

export type getEventDetailV1Response = (getEventDetailV1ResponseSuccess | getEventDetailV1ResponseError)

export const getGetEventDetailV1Url = (id: string,) => {




  return `/api/v1/events/${id}`
}

/**
 * Full detail of one published event by its public GUID: labels, schedule, venue (+ address from Umbraco when the venue is mapped), price/capacity (capacity, availableSpots and guestLimit are returned only for bookingMode Salesforce — null otherwise, whatever the source holds; status Full is set by hand in any mode), hero + media, and the localized description/content blocks (Accept-Language; pt then en fallback; language-neutral blocks always included).
 *
 * NO visibility gate here: any PUBLISHED event opens (deep links, favorites, locked cards from the includeRestricted listing) — only unknown ids and Drafts return 404. visibility (Open / Members Only / Invite Only) is the access discriminator: the FE gates the booking CTA (members-only sheet, invite messaging) from it. Seeing is not booking — the register endpoint keeps the access gate (404 for events the caller cannot register on). Past events remain accessible.
 *
 * Booking guidance for the FE (contract v1.12):
 * - occurrences[]: the event's dated occurrences — the authoritative when/where/how-much (each has its own date, venue, capacity, availableSpots, guestLimit, registrationDeadline, price, isComplimentary, status, bookingRef, seriesId; the three seat counts are null unless bookingMode is Salesforce). The top-level date/venue/price mirror occurrence #1; the AUTHORITATIVE venue is the occurrence's (§3.1.1 — saturdays may happen in one venue and sundays in another). Multi-date events: render the date/room picker from here and register with the chosen occurrence's id.
 * - days[]: the bookable days from today up to 60 days ahead — same rule and format as the listing card's days[] ({ date, startDateTime, endDateTime, occurrenceId, status }). A campaign's future dates exist only here: Salesforce sends no occurrence for them, so occurrences[] holds only the long-past anchor. Offer these days in the "When" step; a derived day has no occurrenceId — SevenRooms campaigns book by date and time (venueNodeKey). Empty for a past event.
 * - groupId/isGroupMain: pass groupId back to the listing to fetch the group's other venues/editions (the "where" step).
 * - bookingMode: the action to open — "Salesforce" (registration flow, POST /me/events/{id}/registrations), "SevenRooms" (reservation stack), "Concierge" (chat), "None" (display only / add-to-calendar) or "External" (open bookingRef). ABSENT from the source (pre-v1.12 events) → "None" (§3.2).
 * - allowsBooking: whether the event shows a booking action — false when bookingMode is None (or absent).
 * - Payment decision order (§3.1.2): 1) isComplimentary → courtesy, no payment; 2) isPaid with price ABSENT → do NOT sell (no CTA, no Stripe — the server also rejects with 422); 3) isPaid with price → normal payment; 4) not paid → free registration.
 * - recurrence: present on recurring events (frequency/interval/weekdays/endDate) so the FE can render the campaign schedule ("EVERYDAY | 7:15pm").
 * - venueNodeKey: the Umbraco venue node that enters the SevenRooms reservation flow — GET /venues/{venueNodeKey}/seating-areas, then availability, then POST /bookings. Null when the venue is not mapped at the bookable (venue) level; the FE falls back to the concierge. Also resolved per occurrence (occurrences[].venueNodeKey) — each occurrence may happen in a different room.
 * - myRegistration: the caller's most recent active registration on this event (id/status/occurrenceId/numberOfGuests/registrationDate) — render the registered/cancel state instead of the book CTA. Null when anonymous, not registered, or only cancelled/expired registrations remain (the caller may register again).
 * @summary Event detail (EDP)
 */
export const getEventDetailV1 = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<getEventDetailV1Response> => {

  return customFetch<getEventDetailV1Response>(getGetEventDetailV1Url(id),
  {
    ...options,
    method: 'GET'


  }
);}


export type getRelatedEventsV1Response200 = {
  data: EventListItemDto[]
  status: 200
}

export type getRelatedEventsV1Response401 = {
  data: void
  status: 401
}

export type getRelatedEventsV1Response404 = {
  data: void
  status: 404
}

export type getRelatedEventsV1ResponseSuccess = (getRelatedEventsV1Response200) & {
  headers: Headers;
};
export type getRelatedEventsV1ResponseError = (getRelatedEventsV1Response401 | getRelatedEventsV1Response404) & {
  headers: Headers;
};

export type getRelatedEventsV1Response = (getRelatedEventsV1ResponseSuccess | getRelatedEventsV1ResponseError)

export const getGetRelatedEventsV1Url = (id: string,
    params?: GetRelatedEventsV1Params,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/events/${id}/related?${stringifiedParams}` : `/api/v1/events/${id}/related`
}

/**
 * Returns upcoming events related to the given event, for the EDP "What's On" rail: same category first, then same event type, then any other upcoming event (the rail stays filled while the catalog has content). The source event is never included; continuous campaigns close the rail. Same visibility rules as the listing.
 *
 * Query Parameters:
 * - limit: number of cards (default 4, max 20)
 *
 * 404 when the source event is unknown or not visible to the caller.
 * @summary Related events for the detail rail
 */
export const getRelatedEventsV1 = async (id: string,
    params?: GetRelatedEventsV1Params, options?: Parameters<typeof customFetch>[1]): Promise<getRelatedEventsV1Response> => {

  return customFetch<getRelatedEventsV1Response>(getGetRelatedEventsV1Url(id,params),
  {
    ...options,
    method: 'GET'


  }
);}


export type getEventCategoriesV1Response200 = {
  data: EventFacetDto[]
  status: 200
}

export type getEventCategoriesV1Response400 = {
  data: void
  status: 400
}

export type getEventCategoriesV1Response401 = {
  data: void
  status: 401
}

export type getEventCategoriesV1ResponseSuccess = (getEventCategoriesV1Response200) & {
  headers: Headers;
};
export type getEventCategoriesV1ResponseError = (getEventCategoriesV1Response400 | getEventCategoriesV1Response401) & {
  headers: Headers;
};

export type getEventCategoriesV1Response = (getEventCategoriesV1ResponseSuccess | getEventCategoriesV1ResponseError)

export const getGetEventCategoriesV1Url = (params?: GetEventCategoriesV1Params,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["eventType","eventCategory","venueId","datePreset","date","visibility"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/events/categories?${stringifiedParams}` : `/api/v1/events/categories`
}

/**
 * Returns each event category (the eventCategory code) with its upcoming-event count (e.g. "F&B (2)"), scoped to the same published + visibility rules as the listing.
 *
 * Accepts the same query parameters as the listing, minus paging, and counts exactly the events GET /events would return for them — so the chip counts can follow the hub, venues, dates, visibility (e.g. visibility=Members Only) and search. Its own eventCategory parameter is ignored: picking a chip never zeroes the others. All optional; with only hubKey/includeRestricted the counts are the upcoming events of the hub, as before.
 *
 * Query Parameters (main ones):
 * - hubKey: location node key — scopes the counts to venues in that hub (optional; invalid GUIDs are ignored)
 * - includeRestricted: true = drop the visibility gate and count every published event, Members Only and Invite Only included — matches a gate-free listing (optional; default keeps the caller-scoped rules)
 * - venueId, datePreset, date, fromDate/toDate, visibility, q, eventType, isCampaign, allowsBooking, excludeExternal, groupId: as in GET /events
 * @summary Category facets for the filter chips
 */
export const getEventCategoriesV1 = async (params?: GetEventCategoriesV1Params, options?: Parameters<typeof customFetch>[1]): Promise<getEventCategoriesV1Response> => {

  return customFetch<getEventCategoriesV1Response>(getGetEventCategoriesV1Url(params),
  {
    ...options,
    method: 'GET'


  }
);}


export type getEventDatesV1Response200 = {
  data: EventDateFacetDto[]
  status: 200
}

export type getEventDatesV1Response400 = {
  data: void
  status: 400
}

export type getEventDatesV1Response401 = {
  data: void
  status: 401
}

export type getEventDatesV1ResponseSuccess = (getEventDatesV1Response200) & {
  headers: Headers;
};
export type getEventDatesV1ResponseError = (getEventDatesV1Response400 | getEventDatesV1Response401) & {
  headers: Headers;
};

export type getEventDatesV1Response = (getEventDatesV1ResponseSuccess | getEventDatesV1ResponseError)

export const getGetEventDatesV1Url = (params?: GetEventDatesV1Params,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["eventType","eventCategory","venueId","datePreset","date","visibility"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/events/dates?${stringifiedParams}` : `/api/v1/events/dates`
}

/**
 * Returns the distinct Europe/Lisbon dates having at least one event matching the active filters, with a count per date — drives the calendar's selectable dates. Multi-date events light every public occurrence day (counted once per event per day). Campaigns light the days their recurrence runs on, from today up to a 60-day horizon: the rail stretches to the furthest date returned here, so an open-ended campaign would otherwise extend the calendar indefinitely. Only days the request asks about are lit: from today (or the explicit fromDate/date), within toDate, and inside the active datePreset/date days — an event matched on a Sunday does not light its Monday. These are exactly the days in the listing cards' days[] for the same filters. Accepts the same query parameters as the listing, minus paging — including includeRestricted, so the calendar can match a gate-free listing, and visibility.
 * @summary Distinct event dates for the calendar
 */
export const getEventDatesV1 = async (params?: GetEventDatesV1Params, options?: Parameters<typeof customFetch>[1]): Promise<getEventDatesV1Response> => {

  return customFetch<getEventDatesV1Response>(getGetEventDatesV1Url(params),
  {
    ...options,
    method: 'GET'


  }
);}


export type getEventTypesV1Response200 = {
  data: EventFacetDto[]
  status: 200
}

export type getEventTypesV1Response400 = {
  data: void
  status: 400
}

export type getEventTypesV1Response401 = {
  data: void
  status: 401
}

export type getEventTypesV1ResponseSuccess = (getEventTypesV1Response200) & {
  headers: Headers;
};
export type getEventTypesV1ResponseError = (getEventTypesV1Response400 | getEventTypesV1Response401) & {
  headers: Headers;
};

export type getEventTypesV1Response = (getEventTypesV1ResponseSuccess | getEventTypesV1ResponseError)

export const getGetEventTypesV1Url = (params?: GetEventTypesV1Params,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["eventType","eventCategory","venueId","datePreset","date","visibility"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/events/event-types?${stringifiedParams}` : `/api/v1/events/event-types`
}

/**
 * Returns each EventType with its upcoming-event count (e.g. "Gala (1)"), scoped to the same published + visibility rules as the listing.
 *
 * Accepts the same query parameters as the listing, minus paging, and counts exactly the events GET /events would return for them; its own eventType parameter is ignored. All optional; with only hubKey/includeRestricted the counts are the upcoming events of the hub, as before.
 *
 * Query Parameters (main ones):
 * - hubKey: location node key — scopes the counts to venues in that hub (optional; invalid GUIDs are ignored)
 * - includeRestricted: true = drop the visibility gate and count every published event, Members Only and Invite Only included — matches a gate-free listing (optional; default keeps the caller-scoped rules)
 * - venueId, datePreset, date, fromDate/toDate, visibility, q, eventCategory, isCampaign, allowsBooking, excludeExternal, groupId: as in GET /events
 * @summary Event-type facets for the filter chips
 */
export const getEventTypesV1 = async (params?: GetEventTypesV1Params, options?: Parameters<typeof customFetch>[1]): Promise<getEventTypesV1Response> => {

  return customFetch<getEventTypesV1Response>(getGetEventTypesV1Url(params),
  {
    ...options,
    method: 'GET'


  }
);}


export type getEventVenuesV1Response200 = {
  data: EventVenueFacetDto[]
  status: 200
}

export type getEventVenuesV1Response400 = {
  data: void
  status: 400
}

export type getEventVenuesV1Response401 = {
  data: void
  status: 401
}

export type getEventVenuesV1ResponseSuccess = (getEventVenuesV1Response200) & {
  headers: Headers;
};
export type getEventVenuesV1ResponseError = (getEventVenuesV1Response400 | getEventVenuesV1Response401) & {
  headers: Headers;
};

export type getEventVenuesV1Response = (getEventVenuesV1ResponseSuccess | getEventVenuesV1ResponseError)

export const getGetEventVenuesV1Url = (params?: GetEventVenuesV1Params,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["eventType","eventCategory","venueId","datePreset","date","visibility"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/events/venues?${stringifiedParams}` : `/api/v1/events/venues`
}

/**
 * Returns the distinct venues of published, visible events starting today (Europe/Lisbon) or later, with counts — the options of the ELP filter overlay. Derived from the events themselves at the venue level (sub-venues — rooms inside a venue — fold into their venue; multi-date events contribute each occurrence's venue, counted once per event), so every listed option is filterable and its count matches the listing filtered by it. `id` is the Salesforce venue id to pass back as `venueId`; `name` matches the venue label shown on the event cards.
 *
 * Accepts the same query parameters as the listing, minus paging — the options and counts follow the other active filters (dates, visibility, category…); its own venueId parameter is ignored, so choosing a venue never hides the others. All optional; with only hubKey the options are as before.
 *
 * Query Parameters (main ones):
 * - hubKey: location node key — scopes the options to venues in that hub (optional; invalid GUIDs are ignored)
 * - includeRestricted: true = drop the visibility gate (optional; default keeps the caller-scoped rules, as before)
 * - datePreset, date, fromDate/toDate, visibility, q, eventType, eventCategory, isCampaign, allowsBooking, excludeExternal, groupId: as in GET /events
 * @summary Venue facets for the filter overlay
 */
export const getEventVenuesV1 = async (params?: GetEventVenuesV1Params, options?: Parameters<typeof customFetch>[1]): Promise<getEventVenuesV1Response> => {

  return customFetch<getEventVenuesV1Response>(getGetEventVenuesV1Url(params),
  {
    ...options,
    method: 'GET'


  }
);}


export type getWhatsOnEventsV1Response200 = {
  data: EventListItemDto[]
  status: 200
}

export type getWhatsOnEventsV1Response401 = {
  data: void
  status: 401
}

export type getWhatsOnEventsV1ResponseSuccess = (getWhatsOnEventsV1Response200) & {
  headers: Headers;
};
export type getWhatsOnEventsV1ResponseError = (getWhatsOnEventsV1Response401) & {
  headers: Headers;
};

export type getWhatsOnEventsV1Response = (getWhatsOnEventsV1ResponseSuccess | getWhatsOnEventsV1ResponseError)

export const getGetWhatsOnEventsV1Url = (params?: GetWhatsOnEventsV1Params,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/events/whats-on?${stringifiedParams}` : `/api/v1/events/whats-on`
}

/**
 * Returns the next N DISTINCT events from today (Europe/Lisbon) onwards, in strict chronological order — the home page "What's On" carousel. One slot per event, at its own next date: a recurring campaign never occupies several slots with its own repeats.
 *
 * Use this, not the listing, for any "next events" rail. GET /events is the calendar surface: it can only order by dates SQL can see, and a recurring campaign reaches us with a long-past start, no materialised occurrence ahead of it and no recurrence end date — so the listing keeps it forever and clamps it to "today", where every such campaign ties and falls back to sort order. Here each event's real next date is resolved first: the earliest upcoming public occurrence, else a date derived from the recurrence descriptor (frequency/interval/weekdays, in Lisbon wall-clock, inheriting the original slot's hour and duration), else its own start. An event with no future date is left out rather than clamped.
 *
 * Query Parameters:
 * - limit: number of cards (default 6, max 50)
 * - excludeExternal: true = leave out events ticketed elsewhere (bookingMode External) — for surfaces that only show what can be booked in the app (optional; default includes them)
 * - includeRestricted: true = drop the visibility gate and return every published event, Members Only and Invite Only included — the FE renders those cards locked (optional; default keeps the caller-scoped rules)
 *
 * Returns one card per GROUP (the isGroupMain event fronts it), as the listing does.
 * @summary Next events for the home page rail
 */
export const getWhatsOnEventsV1 = async (params?: GetWhatsOnEventsV1Params, options?: Parameters<typeof customFetch>[1]): Promise<getWhatsOnEventsV1Response> => {

  return customFetch<getWhatsOnEventsV1Response>(getGetWhatsOnEventsV1Url(params),
  {
    ...options,
    method: 'GET'


  }
);}


